package com.klantenstop.web.pages.admin.curatedcities

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.Dialog
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.curatedcities.CuratedCityTable
import com.klantenstop.web.components.layout.AdminPageLayout
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.materialkobweb.components.toast.ToastManager
import com.materialkobweb.components.widgets.DsSpinner
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.OutlinedButton
import com.materialkobweb.components.widgets.SpinnerSize
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

@Page("/admin/steden")
@Composable
fun CuratedCitiesPage() {
    val ctx = rememberPageContext()
    val viewModel = rememberViewModel { CuratedCitiesViewModel() }
    val state by viewModel.state.collectAsState()

    // This ViewModel is cached across navigation (same instance reused when navigating back from
    // the manage page), so its init block only runs once. Re-trigger the load every time this page
    // is (re)composed to reflect changes made on the manage page without requiring a manual refresh.
    LaunchedEffect(Unit) {
        viewModel.sendIntent(CuratedCitiesContract.Intent.LoadCuratedCities)
    }

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest { effect ->
            when (effect) {
                is CuratedCitiesContract.Effect.NavigateToManage ->
                    ctx.router.navigateTo(Navigation.Screen.Admin.CuratedCities.Manage(effect.curatedCityId).route)

                is CuratedCitiesContract.Effect.ShowError -> ToastManager.error(effect.message)
                is CuratedCitiesContract.Effect.ShowSuccess -> ToastManager.success(effect.message)
            }
        }
    }

    AdminPageLayout(
        title = "Steden",
        fabOnClick = {
            viewModel.sendIntent(CuratedCitiesContract.Intent.NavigateToManage())
        }
    ) {
        CuratedCitiesContent(
            state = state,
            sendIntent = viewModel::sendIntent
        )
    }
}

@Composable
fun CuratedCitiesContent(
    state: CuratedCitiesContract.State,
    sendIntent: (CuratedCitiesContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    Column(
        modifier = Modifier
            .fillMaxWidth(if (breakpoint > Breakpoint.MD) 95.percent else 90.percent)
            .fillMaxHeight()
            .maxWidth(1000.px)
            .padding(leftRight = 24.px, topBottom = 58.px),
        verticalArrangement = Arrangement.spacedBy(32.px)
    ) {
        H1Heading(
            modifier = Modifier,
            typography = Typography.HeadlineSmall,
            text = "Steden",
            color = MaterialColorVars.Primary.value()
        )

        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = if (state.isLoading || state.curatedCities.isEmpty()) Alignment.Center else Alignment.TopCenter
        ) {
            when {
                state.isLoading -> {
                    DsSpinner(size = SpinnerSize.Normal)
                }

                state.curatedCities.isEmpty() -> {
                    SpanText(
                        text = "Geen steden gevonden. Klik op + om er een aan te maken.",
                        modifier = Modifier.color(MaterialColorVars.OnSurfaceVariant.value())
                    )
                }

                else -> {
                    CuratedCityTable(
                        groupedCuratedCities = state.groupedByProvince,
                        onNavigateToEdit = { sendIntent(CuratedCitiesContract.Intent.NavigateToManage(it)) },
                        onRequestDelete = { sendIntent(CuratedCitiesContract.Intent.SetCuratedCityToDelete(it)) }
                    )
                }
            }
        }
    }

    state.curatedCityToDelete?.let { curatedCity ->
        DeleteCuratedCityDialog(
            curatedCityName = curatedCity.name,
            isLoading = state.isLoading,
            onConfirm = { sendIntent(CuratedCitiesContract.Intent.DeleteCuratedCity(curatedCity.id)) },
            onDismiss = { sendIntent(CuratedCitiesContract.Intent.SetCuratedCityToDelete(null)) }
        )
    }
}

@Composable
private fun DeleteCuratedCityDialog(
    curatedCityName: String,
    isLoading: Boolean,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) {
    Dialog(onHideDialog = onDismiss) {
        H2(attrs = Modifier.margin(0.px).toAttrs()) {
            Text("Stad verwijderen")
        }

        SpanText(
            text = "Weet je zeker dat je \"$curatedCityName\" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
            modifier = Modifier
                .margin(top = 16.px, bottom = 32.px)
                .fontSize(16.px)
                .lineHeight(24.px)
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            OutlinedButton(
                modifier = Modifier.flex(1),
                enabled = !isLoading,
                onClick = onDismiss
            ) {
                Text("Annuleren")
            }
            FilledButton(
                modifier = Modifier.flex(1),
                enabled = !isLoading,
                onClick = onConfirm
            ) {
                Text("Verwijderen")
            }
        }
    }
}
